import React, { useEffect, useState, useMemo } from 'react';
import cx from 'classnames';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import {
  checkImageResourceStatus,
  calculatePcImageSize,
  PC_THRESHOLD_HEIGHT,
  ImageResourceStatus,
} from './util';
import CommonReactImage from '@/components/CommontReactImage';
import Styles from './mIndex.module.scss';
import { getOptimizedImageUrl } from '@/utils/image';

const defaultCover = require('../../assets/icon/photo-default-cover.png');
const defaultAuditCover = require('../../assets/icon/photo-default-audit.png');

export interface IImgSize {
  width: number;
  height: number;
  naturalWidth: number;
  naturalHeight: number;
  scale: number;
}
export interface IPhotoPreview {
  className?: string;
  id: string;
  url: string;
  previewUrl?: string;
  ratio?: string;
  isEdit?: boolean;
  percent?: number;
  completed?: boolean;
  error?: any;
  width?: number;
  height?: number;
  onClick?: (imgSize:IImgSize | null) => void;
  onRemove: (key: string) => void;
  onRetry?: (key: string) => void;
  style?: React.CSSProperties | null;
}

const PhotoPreview = ({
  className = '',
  id,
  url,
  previewUrl,
  isEdit,
  onClick = () => {},
  percent = url.indexOf("https://")> -1 ? 1 : 0,
  error,
  onRemove,
  onRetry,
  width = 516,
  completed,
}: IPhotoPreview) => {
  const [imgLoadStatus, setImgLoadStatus] = useState<ImageResourceStatus>();
  const [imgSize, setImgSize] = useState<IImgSize>({} as IImgSize);
  const t = useTranslations();

  const handleClick = () => {
    if (isEdit || !url) {
      return;
    }

    onClick(imgSize);
  };

  const handleRemove = () => {
    onRemove(id);
  };

  const onLoad = (item:any) => {
    const { naturalWidth, naturalHeight, width, height } = item.currentTarget;
    setImgSize({
      naturalWidth: naturalWidth*2,
      naturalHeight: naturalHeight*2,
      width,
      height,
      scale: Number(parseFloat((naturalWidth / width).toString())),
    });
  }

  const customLoader = ({ src }: { src: string }) => {
    return src;
  }

  useEffect(() => {
    // 图片不存在
    if (!url) {
      setImgLoadStatus(ImageResourceStatus.notFound);
      return;
    }

    // 本地图片
    if (url.startsWith('blob:')) {
      setImgLoadStatus(ImageResourceStatus.local);
      return;
    }

    // 检查图片资源状态
    checkImageResourceStatus(url, (statusCode) => setImgLoadStatus(statusCode));
  }, [url])

  const imgStyle = useMemo(() => {
    return calculatePcImageSize(width, imgSize.naturalWidth, imgSize.naturalHeight);
  }, [width, imgSize.naturalWidth, imgSize.naturalHeight]);

  // 判断是否为特定错误码，需要显示错误信息而不是重试
  const isSpecificError = useMemo(() => {
    if (!error) return false;
    
    // 支持多种错误码格式
    const errorCode = error.code || error.error_code || error.errorCode;
    const numericCode = typeof errorCode === 'string' ? parseInt(errorCode) : errorCode;
    
    // 检查错误码
    if (numericCode === 10032 || numericCode === 10033) {
      return true;
    }
    
    // 如果错误是字符串，检查是否包含GIF相关的错误信息
    const errorText = typeof error === 'string' ? error : error.message || '';
    const isGifError = errorText.includes('gif') || errorText.includes('GIF') || 
                      errorText.includes('帧数') || errorText.includes('图片数量');
    
    return isGifError;
  }, [error]);

  return (
    <div
      className={cx(Styles.root, className)}
      style={
        imgStyle
          ? { width: imgStyle.imgWidth, height: imgStyle.imgHeight, visibility: 'visible' }
          : { visibility: (imgLoadStatus || !url ) ? 'visible' : 'hidden' }
      }
    >
      {
        isEdit && <div className={Styles.remove} onClick={handleRemove} />
      }
      {
        (imgLoadStatus && imgLoadStatus !== ImageResourceStatus.success) || !url ?
        <div className={Styles.defaultImgWrapper}>
          {
            imgLoadStatus && imgLoadStatus !== ImageResourceStatus.local && (
              <Image
                src={imgLoadStatus === ImageResourceStatus.audit ? defaultAuditCover : defaultCover}
                loader={customLoader}
                alt="pic"
                width={80}
                height={80}
                className={Styles.img}
                priority
              />
            )
          }
          {
            imgLoadStatus === ImageResourceStatus.audit && <div className={Styles.auditTips}>{t('tips_image_audit')}</div>
          }
        </div>:
        <a
          data-pswp-src={getOptimizedImageUrl(previewUrl || url)}
          data-pswp-width={imgSize?.naturalWidth}
          data-pswp-height={imgSize?.naturalHeight}
          className={cx(Styles.imgWrapper)}
        >
          <CommonReactImage
            className={Styles.img}
            src={url}
            alt="pic"
            onLoad={onLoad}
            onClick={handleClick}
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              maxHeight: `${PC_THRESHOLD_HEIGHT}px`,
            }}
          />
        </a>
      }
      {
        !error ? (
          <>
          {
            (percent < 1 || (isEdit && !completed)) &&
            <>
              <div className={Styles.uploadingMask} />
              <div className={Styles.tips}>{ t('tips_thread_photo_percent', { percent: Math.min(Number.parseInt((percent * 100).toString()), 99) }) }
              </div>
            </>
          }
          </>
        ) : (
          // 如果是特定错误码（10032或10033），显示错误信息，不显示重试按钮
          isSpecificError ? (
            <div className={Styles.uploadRetry}>
              <span style={{color: '#ff4d4f', fontSize: '14px', textAlign: 'center', padding: '8px'}}>
                {typeof error === 'string' ? error : error.message}
              </span>
            </div>
          ) : (
            <div className={Styles.uploadRetry} onClick={()=>onRetry&&onRetry(id)}>
              <i className={Styles.retryIcon} />
              <span>{t("error_upload_retry")}</span>
            </div>
          )
        )
      }
    </div>
  );
};

export default PhotoPreview;
